<template>
  <div class="ui fitted basic segment">
    <!-- Contextual menu -->
    <div
      class="column-menu inline dropdown"
      v-if="allowColumnSelection && columnMenu"
      style="display: block !important; z-index: 100; position: absolute !important;"
      :style="{ left: columnMenu.left, top: columnMenu.top }"
      v-on-clickaway="hideColumnMenu"
    >
      <div class="ui vertical menu">
        <div class="item">
          <div class="header">{{ $tc('CHOOSE_COLUMNS') }}</div>
          <div class="ui icon mini input" v-if="localColumns && localColumns.length > 20">
            <input type="text" :placeholder="$t('SEARCH')" v-model="columnFilterTerm" />
            <i class="search icon"></i>
          </div>
          <div class="scroller menu">
            <draggable v-model="localColumns" @change="onColumnOrderChange">
              <div
                class="item capitalize-fl"
                v-for="c in localFilteredColumns"
                :key="c.key"
                @click="toggleColumn(c)"
              >
                {{ c.name || c.key }}
                <i class="check icon" v-if="!c.hidden"></i>
              </div>
            </draggable>
          </div>
        </div>
      </div>
    </div>

    <div class="ui inverted dimmer" :class="{ active: p.loading }">
      <div class="ui loader"></div>
    </div>
    <table
      class="ui sortable compact small table"
      :class="{ selectable: selectable, definition: selectable }"
    >
      <thead :class="{ scrolled: bodyScroll > 0 }">
        <tr>
          <th id="selectAll" class="collapsing center aligned" @click="selectAll" v-if="selectable">
            <i class="square outline icon" :class="{ check: allSelected }" />
          </th>
          <th
            v-for="c in visibleColumns"
            :key="c.key"
            @click="updateSortBy(c)"
            @contextmenu.prevent="showColumnMenu($event, c)"
            :style="{ width: c.width }"
          >
            <div
              style="float: left; width: calc(100% - 10px); display: inline-block; pointer-events: none;"
              :style="{ width: 'calc(100%' + (sortKey == c.key ? ' - 12px)' : ')') }"
              class="capitalize-fl"
            >
              <slot
                :name="`${(c.template || c.key).toLowerCase().replace('/', '-')}.header`"
                :column="c"
                >{{ c.name || c.key }}</slot
              >
            </div>
            <i
              class="right floated icon"
              style="width: 10px;"
              :class="{
                'sort down': sortKey == c.key && sortAsc,
                'sort up': sortKey == c.key && !sortAsc
              }"
            />
          </th>
        </tr>
      </thead>
      <tbody
        style="display: block; overflow: auto;"
        :style="{ maxHeight: calcMaxHeight }"
        @scroll="onBodyScroll"
        ref="tableBody"
      >
        <template v-if="p.items && p.items.length > 0">
          <tr v-for="row in p.items" :key="row.key">
            <td
              class="selectColumn center aligned"
              @click="$set(row, 'selected', !row.selected)"
              v-if="selectable"
            >
              <i class="square outline icon" :class="{ check: row.selected || allSelected }" />
            </td>
            <td v-for="c in visibleColumns" :key="c.key" :style="{ width: c.width }">
              <i
                class="small right aligned filter grey icon"
                v-if="c.filters"
                v-on:click="filterBy(row, c)"
                style="float: right; margin: 5px;"
              />
              <slot
                :name="`${(c.template || c.key).toLowerCase().replace('/', '-')}`"
                :row="row"
                :cell="get(row, c.key)"
                :value="c.value && c.value(row, reducers[c.key])"
                :reduced="reducers[c.key]"
                :column="c"
              >
                {{ get(row, c.key) }}
              </slot>
            </td>
          </tr>
        </template>
        <!-- NO ITEMS -->
        <tr v-else>
          <td :colspan="visibleColumns ? visibleColumns.length : 1">
            <slot name="norows">
              {{ $t('KITCHENTABLE_EMPTY') }}
            </slot>
          </td>
        </tr>
      </tbody>
      <tfoot :class="{ scrolled: underFoot }">
        <tr>
          <td colspan="10">
            <div class="ui left floated secondary mini menu">
              <div class="item">
                <span id="totalItems" class="capitalize-fl">{{
                  $tc(i18nItemItemCountKey, p.totalItems)
                }}</span>
                <span id="pages" class="capitalize-fl">{{
                  $t('KITCHENTABLE_PAGE', {
                    total: p.totalPages,
                    current: p.currentPage + 1,
                    perPage: p.pageSize
                  })
                }}</span>
              </div>
            </div>
            <div class="ui right floated secondary pagination mini menu" v-if="p.totalPages > 1">
              <a
                class="icon item"
                v-on:click="decrementPagination"
                :class="{ disabled: startPagination == 0 }"
              >
                <i class="left chevron icon" />
              </a>
              <a
                class="item"
                v-for="n in Math.min(paginationPagesShown, p.totalPages || 0)"
                :key="n"
                :class="{ active: p.currentPage + 1 == startPagination + n }"
                v-on:click="updatePage(startPagination + n - 1)"
              >
                {{ startPagination + n }}
              </a>
              <a
                class="icon item"
                v-on:click="incrementPagination"
                :class="{ disabled: startPagination + paginationPagesShown >= p.totalPages }"
              >
                <i class="right chevron icon"></i>
              </a>
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../../scss/variables';

#pages {
  margin-left: 10px;
}

th#selectAll,
td.selectColumn {
  width: 35px;
  pointer-events: auto;
}

thead th > div {
  overflow: hidden;
  text-overflow: ellipsis;
}

tbody td {
  overflow: hidden;
  text-overflow: ellipsis;
}

thead,
tbody tr {
  display: table;
  width: 100%;
  table-layout: fixed; /* even columns width , fix width of table too */
}

table {
  height: calc(100% - 10px);
  thead.scrolled {
    box-shadow: 0 3px 5px -2px rgba(0, 0, 0, 0.3);
    transition: 0.2s filter linear;
  }

  tbody {
    display: block;
    overflow-y: scroll;
    overflow-x: auto;
  }

  tfoot.scrolled {
    box-shadow: 0 -3px 5px -2px rgba(0, 0, 0, 0.3);
    transition: 0.2s filter linear;
  }
}
.column-menu {
  position: absolute !important; // Overrule fomantic so we can position dynamically
}

.scroller {
  max-height: 150px;
  overflow-y: auto;
  overflow-x: hidden;
  text-overflow: ellipsis;
}
</style>

<script>
// @flow
import _ from 'lodash'
import Vue from 'vue'
import { mixin as clickaway } from 'vue-clickaway'
import draggable from 'vuedraggable'
import { Toast } from 'services'

const isOverflowingBottom = ({ clientHeight, scrollHeight, scrollTop }) =>
  scrollHeight > clientHeight + scrollTop

export function KitchenPage(page) {
  this.items = page ? page.items : []
  this.totalPages = page ? page.totalPages : 0
  this.totalItems = page ? page.totalItems : 0
  this.pageSize = page ? page.pageSize : 0
  this.currentPage = page ? page.currentPage : 0
  this.sortBy = page ? page.sortBy : null
  this.header = page ? page.header : []

  if (page && (page.loading === true || page.loading === false)) {
    this.loading = page.loading
  } else {
    this.loading = false
  }

  this.goto = page ? page.goto : null
  this.sort = page ? page.sort : null
}

export function InMemoryPager(page) {
  const startSlicingAt = (page.currentPage || 0) * page.pageSize
  this.currentPage = page.currentPage || 0
  this.items = _.slice(page.items, startSlicingAt, startSlicingAt + page.pageSize)
  this.pageSize = page.pageSize
  this.sortBy = page.sortBy
  this.loading = page.loading

  // Calculate the rest
  this.totalItems = page.items.length
  this.totalPages = Math.ceil(page.items.length / page.pageSize)

  // goto another page func
  this.goto = pageNumber =>
    new Promise(resolve =>
      resolve(
        new KitchenPage(new InMemoryPager({ ...page, items: page.items, currentPage: pageNumber }))
      )
    )

  // sort
  this.sort = (f, sortBy, sortAsc) =>
    new Promise(resolve =>
      resolve(
        new KitchenPage(
          new InMemoryPager({
            ...page,
            sortBy,
            items: sortAsc ? _.reverse(_.sortBy(page.items, f)) : _.sortBy(page.items, f)
          })
        )
      )
    )
}

export default {
  name: 'kitchen-table',

  mixins: [clickaway],

  components: { draggable },

  props: {
    selectable: { type: Boolean, default: () => false },
    maxHeight: { type: String, default: () => '100vh' },
    page: {
      type: KitchenPage,
      default: () => {
        return new KitchenPage()
      }
    },
    columns: { type: Array, default: () => [] },
    i18nItemItemCountKey: { type: String, default: () => 'KITCHENTABLE_ITEMCOUNT' },
    allowColumnSelection: { type: Boolean, default: () => false }
  },

  data() {
    return {
      startPagination: 0,
      paginationPagesShown: 5,
      allSelected: false,
      p: this.page,
      bodyScroll: 0,
      underFoot: false, // TODO figure out better default value if possible
      columnMenu: null,
      localColumns: this.columns,
      columnFilterTerm: ''
    }
  },

  computed: {
    reducers() {
      const reducers = _.keyBy(
        _.map(
          _.filter(this.localColumns, c => c.reducer), // only those with reducers
          c => ({ key: c.key, ..._.reduce(this.page.items, c.reducer, {}) })
        ),
        'key'
      )
      return reducers
    },
    visibleColumns() {
      return _.filter(this.columns, c => !c.hidden)
    },
    fixedHeader() {
      return this.maxHeight
    },
    calcMaxHeight() {
      return `calc(${this.maxHeight} - 100px)`
    },
    sortKey() {
      return this.p && this.p.sortBy ? this.p.sortBy.replace('!', '') : ''
    },
    sortAsc() {
      return this.p && this.p.sortBy ? this.p.sortBy.indexOf('!') < 0 : false
    },
    localFilteredColumns() {
      if (this.columnFilterTerm) {
        return _.filter(
          this.localColumns,
          c => c.key && c.key.toLowerCase().indexOf(this.columnFilterTerm.toLowerCase()) >= 0
        )
      }

      return this.localColumns
    }
  },

  methods: {
    get(o, key) {
      return _.get(o, key)
    },
    onColumnOrderChange(evt) {
      // Figure out new order properties
      const withOrder = _.map(this.localColumns, (col, idx) => ({ ...col, order: idx }))
      this.$emit('column-order-changed', withOrder)
    },
    toggleColumn(c) {
      //this.$set(c, 'hidden', !c.hidden)
      this.$emit('column-changed', { ...c, hidden: !c.hidden })
    },
    showColumnMenu(evt, column) {
      this.columnMenu = {
        left: evt.target.offsetLeft + 10 + 'px',
        top: evt.target.offsetTop + 50 + 'px',
        column
      }
    },
    hideColumnMenu() {
      this.columnMenu = null
    },
    onBodyScroll() {
      this.bodyScroll = this.$refs.tableBody.scrollTop
      this.underFoot = isOverflowingBottom(this.$refs.tableBody)
    },

    filterBy(row, column) {
      this.$emit('filter-by', { row, column })
    },

    selectAll() {
      this.allSelected = !this.allSelected
    },

    updatePage(num) {
      if (this.p.goto) {
        this.p.loading = true
        this.p.goto(num).then(pg => {
          this.p = pg
          this.p.loading = false
        })
      }
      this.$emit('goto-page', num)
    },

    updateSortBy(column) {
      let sortAsc = this.sortAsc

      if (!column && this.p.sortBy) {
        const colKey = this.p.sortBy.replace('!', '')
        column = _.find(this.columns, col => col.key == colKey)
        sortAsc = this.p.sortBy.indexOf('!') !== -1
      }

      if (!column) return

      if (!column.sorts) {
        Toast.warning(
          this.$i18n.t('WARN_COLUMN_NOT_SORTABLE', {
            column: column.name || column.key
          })
        )
        return
      }

      // Switch ASC/DESC
      let sortKey = column.key

      if (this.sortKey == sortKey) sortAsc = !sortAsc

      if (this.sortKey == sortKey && this.sortAsc == sortAsc) return

      // Update sortBy on p
      this.p.sortBy = (sortAsc ? '' : '!') + sortKey

      // Sort if the page has built-in support for it
      if (this.p.sort) {
        this.p.loading = true
        this.p.sort(column.sortValue || column.key, this.p.sortBy, sortAsc).then(pg => {
          this.p = pg
          this.p.loading = false
        })
      }

      this.$emit('sort-by', { by: sortKey, ascending: sortAsc })
    },

    incrementPagination() {
      this.startPagination = Math.min(
        this.startPagination + 1,
        this.p.totalPages - this.paginationPagesShown
      )
    },

    decrementPagination() {
      this.startPagination = Math.max(this.startPagination - 1, 0)
    }
  },

  watch: {
    page: function(p) {
      this.p = p
      this.updateSortBy()
    },
    columns: function(c) {
      this.localColumns = c
    }
  }
}
</script>
